import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
  ShieldCheck, Send, Loader2, Calendar, Users, ListChecks, UserCheck,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import HussarTopNav from '../components/HussarTopNav';
import { track } from '../lib/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PilotPubliczny = () => {
  const { t } = useTranslation();

  useEffect(() => {
    track('gov_pilot_page_view', {});
    document.title = t('gov.pilot.metaTitle');
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', t('gov.pilot.metaDescription'));
  }, [t]);

  const weeks = t('gov.pilot.weeks', { returnObjects: true });
  const candidates = t('gov.pilot.candidates.items', { returnObjects: true });

  return (
    <div className="hussar-landing" data-testid="gov-pilot-page">
      <HussarTopNav active="pilot" />

      <main className="hussar-agenci-main">
        <section className="hussar-agenci-hero" data-testid="gov-pilot-hero">
          <div className="hussar-agenci-eyebrow">
            <ShieldCheck className="w-3 h-3 inline-block mr-1" />
            {t('gov.pilot.eyebrow')}
          </div>
          <h1 className="hussar-agenci-h1" data-testid="gov-pilot-h1">{t('gov.pilot.h1')}</h1>
          <p className="hussar-agenci-subhead" data-testid="gov-pilot-subhead">{t('gov.pilot.subhead')}</p>
        </section>

        <section className="hussar-pol-op-section" data-testid="gov-pilot-weeks">
          <h2 className="hussar-agenci-h2">
            <Calendar className="w-6 h-6 inline-block mr-2" />
            {t('gov.pilot.weeksHeading')}
          </h2>
          <div className="hussar-agenci-deliverables-grid">
            {weeks.map((w, i) => (
              <Card key={w.label} className="hussar-agenci-deliverable-card" data-testid={`gov-pilot-week-${i}`}>
                <div className="hussar-agenci-deliverable-status">{w.label}</div>
                <p className="hussar-agenci-deliverable-body">{w.body}</p>
              </Card>
            ))}
          </div>
        </section>

        <section className="hussar-pol-op-section" data-testid="gov-pilot-candidates">
          <h2 className="hussar-agenci-h2">
            <ListChecks className="w-6 h-6 inline-block mr-2" />
            {t('gov.pilot.candidates.title')}
          </h2>
          <ul className="hussar-pol-op-bullets">
            {candidates.map((c, i) => (
              <li key={c} data-testid={`gov-pilot-candidate-${i}`}>{c}</li>
            ))}
          </ul>
        </section>

        {/* v4.0 Phase 5 — Formats for immigration record integrity pilots. */}
        <section className="hussar-pol-op-section" data-testid="gov-pilot-types-v4">
          <p data-testid="gov-pilot-polish-frame" style={{
            fontSize: 11, letterSpacing: 1.6, fontWeight: 700,
            color: '#7DD3FC', textTransform: 'uppercase', margin: '0 0 10px',
          }}>
            {t('gov.pilot.pilotV4.polishFrame')}
          </p>
          <h2 className="hussar-agenci-h2" data-testid="gov-pilot-types-v4-title">
            <Users className="w-6 h-6 inline-block mr-2" />
            {t('gov.pilot.pilotV4.typesTitle')}
          </h2>
          <p className="hussar-pol-op-body" data-testid="gov-pilot-types-v4-intro">
            {t('gov.pilot.pilotV4.typesIntro')}
          </p>
          <ul className="hussar-pol-op-bullets" data-testid="gov-pilot-types-v4-list">
            {(t('gov.pilot.pilotV4.types', { returnObjects: true }) || []).map((item, i) => (
              <li key={item} data-testid={`gov-pilot-type-v4-${i}`}>{item}</li>
            ))}
          </ul>
        </section>

        {/* v4.0 Phase 5 — What the pilot actually tests. */}
        <section className="hussar-pol-op-section" data-testid="gov-pilot-workflow-v4">
          <h2 className="hussar-agenci-h2" data-testid="gov-pilot-workflow-v4-title">
            <UserCheck className="w-6 h-6 inline-block mr-2" />
            {t('gov.pilot.pilotV4.workflowTitle')}
          </h2>
          <ul className="hussar-pol-op-bullets" data-testid="gov-pilot-workflow-v4-list">
            {(t('gov.pilot.pilotV4.workflow', { returnObjects: true }) || []).map((item, i) => (
              <li key={item} data-testid={`gov-pilot-workflow-v4-${i}`}>{item}</li>
            ))}
          </ul>
          <p className="hussar-pol-op-body" data-testid="gov-pilot-safety-line-v4"
             style={{ marginTop: '18px', fontWeight: 600, color: 'rgba(226,232,240,0.92)' }}>
            {t('gov.pilot.pilotV4.safetyLine')}
          </p>
          <p className="hussar-pol-op-body" data-testid="gov-pilot-integrity-line-v4"
             style={{ marginTop: '8px', fontStyle: 'italic', color: 'rgba(226,232,240,0.82)' }}>
            {t('gov.pilot.pilotV4.integrityLine')}
          </p>
        </section>

        <PilotForm />

        <footer className="hussar-pol-op-footer" data-testid="gov-pilot-footer">
          <p>{t('gov.pilot.form.footerLine')}</p>
          <p className="hussar-pol-op-footer-meta">{t('gov.pilot.form.footerMeta')}</p>
        </footer>
      </main>
    </div>
  );
};

const initialForm = {
  fullName: '', institution: '', role: '', email: '',
  processType: '', problemDescription: '', canAnonymise: false, consent: false,
};

const PilotForm = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.consent) {
      setError(t('gov.pilot.form.errorConsent'));
      return;
    }
    if (!form.fullName.trim() || !form.institution.trim() || !form.email.trim()) {
      setError(t('gov.pilot.form.errorRequiredFields'));
      return;
    }
    setSubmitting(true);
    track('gov_pilot_submitted', {});
    try {
      await axios.post(`${API}/gov-triage/pilot`, form);
      setSuccess(true);
      track('gov_pilot_success', {});
    } catch (e) {
      setError(e?.response?.data?.detail || t('gov.pilot.form.errorSubmit'));
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <section className="hussar-pol-op-section" data-testid="gov-pilot-success">
        <Card className="hussar-tc-card">
          <div className="hussar-compliance-output-badge"><UserCheck className="w-4 h-4" /> {t('gov.pilot.form.successTitle')}</div>
          <p className="hussar-pol-op-body" style={{ marginTop: '12px' }}>{t('gov.pilot.form.successBody')}</p>
        </Card>
      </section>
    );
  }

  return (
    <section className="hussar-pol-op-section" data-testid="gov-pilot-form-section">
      <h2 className="hussar-agenci-h2">
        <Users className="w-6 h-6 inline-block mr-2" />
        {t('gov.pilot.form.title')}
      </h2>
      <Card className="hussar-tc-card hussar-compliance-form" data-testid="gov-pilot-form">
        <form onSubmit={submit}>
          <Field label={t('gov.pilot.form.fullNameLabel')}>
            <Input value={form.fullName} onChange={(e) => upd('fullName', e.target.value)}
                   data-testid="gov-pilot-fullName" />
          </Field>
          <Field label={t('gov.pilot.form.institutionLabel')}>
            <Input value={form.institution} onChange={(e) => upd('institution', e.target.value)}
                   data-testid="gov-pilot-institution" />
          </Field>
          <Field label={t('gov.pilot.form.roleLabel')}>
            <Input value={form.role} onChange={(e) => upd('role', e.target.value)}
                   data-testid="gov-pilot-role" />
          </Field>
          <Field label={t('gov.pilot.form.emailLabel')}>
            <Input type="email" value={form.email} onChange={(e) => upd('email', e.target.value)}
                   data-testid="gov-pilot-email" />
          </Field>
          <Field label={t('gov.pilot.form.processTypeLabel')}>
            <Input value={form.processType} onChange={(e) => upd('processType', e.target.value)}
                   data-testid="gov-pilot-processType" />
          </Field>
          <Field label={t('gov.pilot.form.problemLabel')}>
            <Textarea rows={4} value={form.problemDescription}
                      onChange={(e) => upd('problemDescription', e.target.value)}
                      data-testid="gov-pilot-problem" />
          </Field>
          <div className="hussar-compliance-consent">
            <Checkbox id="gov-pilot-anon" checked={form.canAnonymise}
                      onCheckedChange={(v) => upd('canAnonymise', !!v)}
                      data-testid="gov-pilot-anon" />
            <label htmlFor="gov-pilot-anon">{t('gov.pilot.form.anonLabel')}</label>
          </div>
          <div className="hussar-compliance-consent">
            <Checkbox id="gov-pilot-consent" checked={form.consent}
                      onCheckedChange={(v) => upd('consent', !!v)}
                      data-testid="gov-pilot-consent" />
            <label htmlFor="gov-pilot-consent">{t('gov.pilot.form.consentLabel')}</label>
          </div>
          {error && <p className="hussar-compliance-error" data-testid="gov-pilot-error">{error}</p>}
          <div style={{ marginTop: '16px' }}>
            <Button type="submit" size="lg" className="hussar-btn-primary"
                    disabled={submitting} data-testid="gov-pilot-submit">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {submitting ? t('gov.pilot.form.submitting') : t('gov.pilot.form.submit')}
            </Button>
          </div>
        </form>
      </Card>
    </section>
  );
};

const Field = ({ label, children }) => (
  <div className="hussar-cb-field">
    <label className="hussar-form-label">{label}</label>{children}
  </div>
);

export default PilotPubliczny;
